import { useState, useCallback, useEffect } from 'react';

/**
 * Custom hook for managing bulk selection in tables
 * @param {Array} items - Array of items to select from
 * @param {string} idKey - Key to use as unique identifier (default: 'id')
 * @returns {Object} Selection state and handlers
 */
const useBulkSelection = (items = [], idKey = 'id') => {
  const [selectedItems, setSelectedItems] = useState(new Set());

  // Clear selection for items that no longer exist (but keep selection for items that still exist)
  useEffect(() => {
    setSelectedItems(prev => {
      const currentIds = new Set(items.map(item => item[idKey]).filter(Boolean));
      // Only keep IDs that still exist in current items
      const filtered = new Set();
      prev.forEach(id => {
        if (currentIds.has(id)) {
          filtered.add(id);
        }
      });
      // Only update if selection actually changed (to prevent unnecessary re-renders)
      if (filtered.size !== prev.size) {
        return filtered;
      }
      return prev;
    });
  }, [items, idKey]);

  // Check if an item is selected
  const isSelected = useCallback((itemId) => {
    return selectedItems.has(itemId);
  }, [selectedItems]);

  // Toggle selection of a single item
  const toggleSelection = useCallback((itemId) => {
    setSelectedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  }, []);

  // Select a single item
  const selectItem = useCallback((itemId) => {
    setSelectedItems(prev => new Set([...prev, itemId]));
  }, []);

  // Deselect a single item
  const deselectItem = useCallback((itemId) => {
    setSelectedItems(prev => {
      const newSet = new Set(prev);
      newSet.delete(itemId);
      return newSet;
    });
  }, []);

  // Select all items from the provided array
  const selectAll = useCallback((itemsToSelect = items) => {
    const allIds = itemsToSelect.map(item => item[idKey]).filter(Boolean);
    setSelectedItems(new Set(allIds));
  }, [items, idKey]);

  // Deselect all items
  const deselectAll = useCallback(() => {
    setSelectedItems(new Set());
  }, []);

  // Toggle select all (selects all if none selected, deselects all if some/all selected)
  const toggleSelectAll = useCallback((itemsToSelect = items) => {
    setSelectedItems(prev => {
      const allIds = itemsToSelect.map(item => item[idKey]).filter(Boolean);
      if (allIds.length === 0) return prev;
      
      const allSelected = allIds.every(id => prev.has(id));
      
      if (allSelected) {
        // Deselect all
        return new Set();
      } else {
        // Select all
        return new Set(allIds);
      }
    });
  }, [items, idKey]);

  // Check if all items are selected
  const isAllSelected = useCallback((itemsToCheck = items) => {
    if (itemsToCheck.length === 0) return false;
    const allIds = itemsToCheck.map(item => item[idKey]).filter(Boolean);
    return allIds.length > 0 && allIds.every(id => selectedItems.has(id));
  }, [items, idKey, selectedItems]);

  // Check if some items are selected (but not all)
  const isSomeSelected = useCallback((itemsToCheck = items) => {
    if (itemsToCheck.length === 0) return false;
    const allIds = itemsToCheck.map(item => item[idKey]).filter(Boolean);
    const selectedCount = allIds.filter(id => selectedItems.has(id)).length;
    return selectedCount > 0 && selectedCount < allIds.length;
  }, [items, idKey, selectedItems]);

  // Get selected items as array
  const getSelectedItems = useCallback(() => {
    return Array.from(selectedItems);
  }, [selectedItems]);

  // Get selected items data (full objects)
  const getSelectedItemsData = useCallback((itemsData = items) => {
    return itemsData.filter(item => selectedItems.has(item[idKey]));
  }, [items, idKey, selectedItems]);

  // Get count of selected items
  const selectedCount = selectedItems.size;

  // Clear selection when items change (optional - can be disabled)
  const clearSelectionOnItemsChange = useCallback(() => {
    setSelectedItems(new Set());
  }, []);

  return {
    selectedItems,
    selectedCount,
    isSelected,
    toggleSelection,
    selectItem,
    deselectItem,
    selectAll,
    deselectAll,
    toggleSelectAll,
    isAllSelected,
    isSomeSelected,
    getSelectedItems,
    getSelectedItemsData,
    clearSelectionOnItemsChange
  };
};

export default useBulkSelection;

